import ArrowLeft from '@mui/icons-material/ArrowLeft';
import { IconButton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import './Leaderboard.css';
import {
  AgentSlide,
  IUserData,
  SkeletonSlide,
  SlideEvent,
  SlideMetric,
  SlideObservable,
  SlideType,
} from './Slide';
import SlideContainer from './SlideContainer';
import Agent, {
  getAllAnsweredLabel,
  getAllCallsLabel,
  getAllConnectionsLabel,
  getAllDurationAvgLabel,
  getAllDurationAvgMinLabel,
  getAllDurationSumLabel,
  getAllIncomingLabel,
  getAllMissedLabel,
  getAllOutgoingLabel,
  getDurationMilliseconds,
  getIncomingAnsweredLabel,
  getIncomingConnectionLabel,
  getIncomingMissedLabel,
  getOutgoingAnsweredLabel,
  getOutgoingConnectionLabel,
  getOutgoingMissedLabel,
  getTotalDurationLabel,
} from './slides/Agent';
import Queue from './slides/Queue';
import Skeleton from './slides/Skeleton';
import useLeaderboard, {
  getbusinessuserreporting2,
  ILeaderboard,
} from './useLeaderboard';
import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';

const useStyles = makeStyles((theme: Theme) => ({
  leaderboardDisplayContainer: {
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    border: 0,
    backgroundColor: '#f7f5f5',
  },
  error: {
    color: 'red',
    fontSize: 20,
    padding: theme.spacing(1),
  },
  exitButton: {
    position: 'absolute',
    bottom: theme.spacing(1),
    left: theme.spacing(1),
    zIndex: 100,
  },
}));

const KIXIE_LOADING_BANNER_WAIT_TIME = 3000;
const SLIDE_ANIMATE_IN_MS = 1500;
const SLIDE_ANIMATE_OUT_MS = 1500;

const SKELETON_SLIDE_OBSERVABLE = new SlideObservable({
  slideId: 'skeleton',
  type: SlideType.skeleton,
  active: true,
  slideDurationInSec: -1,
  slideName: 'Skeleton',
} as SkeletonSlide);

const SKELETON_LEADERBOARD: ILeaderboard = {
  name: '',
  leaderboardId: -1,
  slideObservables: [SKELETON_SLIDE_OBSERVABLE],
  fetchedAt: new Date(),
  createDate: new Date().toISOString(),
  createdBy: '',
  businessId: -1,
  timeZone: '',
};

let newUsersData: any[] = [];

const pad = (num) => {
  return ('0' + num).slice(-2);
};

export const hhmmss = (secs) => {
  let minutes = Math.floor(secs / 60);
  secs = Math.floor(secs % 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    minutes = Math.floor(minutes % 60);
    return `${hours}:${pad(minutes)}:${pad(secs)}`;
  }
  return `${minutes}:${pad(secs)}`;
};

interface ILeaderboardParams {
  id: string;
}

export const cleanSlideMetric = (metric: string): SlideMetric => {
  if (metric.indexOf(',') !== -1) {
    return metric.split(',')[1] as SlideMetric;
  }
  return metric as SlideMetric;
};

export default function Leaderboard() {
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };

  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );

  const callData = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };

  const users = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(callData),
    })
  );

  const BusinessData = Object.assign(
    {},
    businessData?.contents?.response?.data?.result?.[0] || {}
  );

  const BusinessUsers = users?.contents?.response?.data?.result || [];

  const { treatment: treatmentDateRange } = useFeatureFlag(
    'SWARM-43-leaderboard-filtering-date-range'
  );
  const isNewDateRangeEnable =
    treatmentDateRange === 'control' || treatmentDateRange === 'on';

  const getValues = async (slide: AgentSlide): Promise<any[] | undefined> => {
    const response = await getbusinessuserreporting2(
      {
        businessid: userAtom?.businessid,
        apikey: userAtom?.apikey,
      },
      {
        connectionDuration: BusinessData?.connectionDuration,
        timezone_php: BusinessData?.timezone_php,
      },
      isNewDateRangeEnable
    );

    const CallReport = Object.assign({}, response?.stats?.users || {});

    const ActiveUsers =
      BusinessUsers.filter((u) => {
        if (!u.isactive) return false;

        if (
          slide.teamId &&
          (typeof u.teams !== 'string' ||
            u.teams.split(',').indexOf(slide.teamId.toString()) === -1)
        ) {
          return false;
        }

        return true;
      }) || [];

    const usersData: any[] = [];

    ActiveUsers.forEach((user) => {
      const e = {
        userid: user.userid,
        fname: user.fname,
        lname: user.lname,
        allanswered: 0,
        allconnections: 0,
        alldurationavg: 0,
        alldurationsum: 0,
        allincoming: 0,
        allmissed: 0,
        alloutgoing: 0,
        incominganswered: 0,
        incomingconnection: 0,
        incomingmissed: 0,
        outgoinganswered: 0,
        outgoingconnection: 0,
        outgoingmissed: 0,
        total: 0,
        ...CallReport[user.userid],
      };

      const stats: any = {};
      stats.allcalls = e.total || 0;
      stats.allconnections = e.allconnections || 0;
      stats.allavgduration = e.alldurationavg || 0;
      stats.alldurationavg = e.alldurationavg || 0;
      stats.alldurationavg2 = e.alldurationavg2 || 0;
      stats.avgduration = hhmmss(Math.ceil(stats.alldurationavg2));

      stats.alldurationavgmin = parseFloat(
        (stats.allavgduration / 60).toFixed(2)
      );

      const tduration = stats.allavgduration * stats.allcalls;
      const tdurationread = hhmmss(Math.ceil(tduration));

      stats.tdurationread = tdurationread;
      stats.tduration = tduration;
      usersData.push({
        ...e,
        ...stats,
      });
    });

    newUsersData = _.sortBy(usersData, slide.metric).reverse();
    newUsersData.forEach((obj, i) => {
      const stat: IUserData[] = [];

      const addAllCallsStat = () =>
        stat.push({
          title: getAllCallsLabel(obj),
          value: obj.allcalls,
          metric: slide.metric,
        });

      const addAllAnsweredStat = () =>
        stat.push({
          title: getAllAnsweredLabel(obj),
          value: obj.allanswered,
          metric: slide.metric,
        });

      const addAllConnectionsStat = () =>
        stat.push({
          title: getAllConnectionsLabel(obj),
          value: obj.allconnections,
          metric: slide.metric,
        });

      const addAllDurationAvgStat = () =>
        stat.push({
          title: getAllDurationAvgLabel(obj),
          value: hhmmss(obj.alldurationavg),
          metric: slide.metric,
        });

      const addAllDurationSumStat = () =>
        stat.push({
          title: getAllDurationSumLabel(obj),
          value: obj.alldurationsum,
          metric: slide.metric,
        });

      const addAllIncomingStat = () =>
        stat.push({
          title: getAllIncomingLabel(obj),
          value: obj.allincoming,
          metric: slide.metric,
        });

      const addAllMissedStat = () =>
        stat.push({
          title: getAllMissedLabel(obj),
          value: obj.allmissed,
          metric: slide.metric,
        });

      const addAllOutgoingStat = () =>
        stat.push({
          title: getAllOutgoingLabel(obj),
          value: obj.alloutgoing,
          metric: slide.metric,
        });

      const addIncomingAnsweredStat = () =>
        stat.push({
          title: getIncomingAnsweredLabel(obj),
          value: obj.incominganswered,
          metric: slide.metric,
        });

      const addIncomingConnectionStat = () =>
        stat.push({
          title: getIncomingConnectionLabel(obj),
          value: obj.incomingconnection,
          metric: slide.metric,
        });

      const addIncomingMissedStat = () =>
        stat.push({
          title: getIncomingMissedLabel(obj),
          value: obj.incomingmissed,
          metric: slide.metric,
        });

      const addOutgoingAnsweredStat = () =>
        stat.push({
          title: getOutgoingAnsweredLabel(obj),
          value: obj.outgoinganswered,
          metric: slide.metric,
        });

      const addOutgoingConnectionStat = () =>
        stat.push({
          title: getOutgoingConnectionLabel(obj),
          value: obj.outgoingconnection,
          metric: slide.metric,
        });

      const addOutgoingMissedStat = () =>
        stat.push({
          title: getOutgoingMissedLabel(obj),
          value: obj.outgoingmissed,
          metric: slide.metric,
        });

      const addDurationStat = () =>
        stat.push({
          title: 'Duration',
          value: obj.tdurationread,
          metric: slide.metric,
        });

      const addTotalDurationStat = () =>
        stat.push({
          title: getTotalDurationLabel(obj),
          value: hhmmss(obj.tduration),
          metric: slide.metric,
        });

      const addAllDurationAvgMinStat = () =>
        stat.push({
          title: getAllDurationAvgMinLabel(obj),
          value: hhmmss(obj.alldurationavg),
          metric: slide.metric,
        });

      switch (slide.metric) {
        case SlideMetric.allcalls:
          addDurationStat();
          addAllCallsStat();
          break;
        case SlideMetric.allanswered:
          addDurationStat();
          addAllAnsweredStat();
          break;
        case SlideMetric.allconnections:
          addDurationStat();
          addAllConnectionsStat();
          break;
        case SlideMetric.alldurationavg:
          addAllDurationAvgStat();
          break;
        case SlideMetric.alldurationsum:
          addAllDurationSumStat();
          break;
        case SlideMetric.allincoming:
          addDurationStat();
          addAllIncomingStat();
          break;
        case SlideMetric.allmissed:
          addAllMissedStat();
          break;
        case SlideMetric.alloutgoing:
          addDurationStat();
          addAllOutgoingStat();
          break;
        case SlideMetric.incominganswered:
          addDurationStat();
          addIncomingAnsweredStat();
          break;
        case SlideMetric.incomingconnection:
          addDurationStat();
          addIncomingConnectionStat();
          break;
        case SlideMetric.incomingmissed:
          addIncomingMissedStat();
          break;
        case SlideMetric.outgoinganswered:
          addDurationStat();
          addOutgoingAnsweredStat();
          break;
        case SlideMetric.outgoingconnection:
          addDurationStat();
          addOutgoingConnectionStat();
          break;
        case SlideMetric.outgoingmissed:
          addOutgoingMissedStat();
          break;
        case SlideMetric.tduration:
          addTotalDurationStat();
          break;
        case SlideMetric.alldurationavgmin:
          addAllDurationAvgMinStat();
          break;
        default:
          throw new Error(`unsupported metric: ${slide.metric}`);
      }

      newUsersData[i].data = stat;
    });

    return newUsersData;
  };
  // END Taken from existing leaderboard and modified

  const classes = useStyles();

  const { id: leaderboardId } = useParams<ILeaderboardParams>();

  const [isReady, setIsReady] = useState(false);
  const [hasInitialFetched, setHasInitialFetched] = useState(false);
  const [leaderboard, setLeaderboard] = useState<ILeaderboard>(
    SKELETON_LEADERBOARD
  );
  const [nextLeaderboard, setNextLeaderboard] = useState<ILeaderboard>();
  const [leaderboardError, setLeaderboardError] = useState<Error>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const leaderboardRef = useRef<ILeaderboard>();
  leaderboardRef.current = leaderboard;

  const userAtom = useRecoilValueLoadable(user).contents as IUserAtom;

  async function fetchLeaderboard() {
    try {
      return await useLeaderboard(
        userAtom.businessid,
        userAtom.apikey,
        BusinessData?.timezone_php,
        leaderboardId,
        getValues
      );
    } catch (err: any) {
      setLeaderboardError(err);
    }
  }

  useEffect(() => {
    if (
      hasInitialFetched ||
      !userAtom.businessid ||
      !BusinessData?.timezone_php ||
      users.state !== 'hasValue'
    ) {
      return;
    }

    setHasInitialFetched(true);

    fetchLeaderboard().then((leaderboard) => {
      if (leaderboard) {
        setCurrentSlideIndex(-1);
        setNextLeaderboard(leaderboard);
      }
    });
  }, [hasInitialFetched, userAtom, businessData, users.state]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsReady(true);
    }, KIXIE_LOADING_BANNER_WAIT_TIME);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!isReady || currentSlideIndex === -1) return;

    const slideObservables = leaderboardRef.current?.slideObservables ?? [];

    const slideObservable = slideObservables[currentSlideIndex];

    if (!slideObservable) return;

    slideObservable.notify(SlideEvent.animateInStarted);

    const animatedInTimeout = setTimeout(() => {
      slideObservable.notify(SlideEvent.animatedIn);
    }, SLIDE_ANIMATE_IN_MS);

    if (slideObservable === SKELETON_SLIDE_OBSERVABLE) return;

    const duration = getDurationMilliseconds(
      slideObservable.slide,
      (slideObservable.slide as AgentSlide)?.pages ?? []
    );

    const animateOutTimeout = setTimeout(() => {
      slideObservable.notify(SlideEvent.animateOutStarted);
    }, SLIDE_ANIMATE_IN_MS + duration.slideDurationMs);

    const animatedOutTimeout = setTimeout(() => {
      slideObservable.notify(SlideEvent.animatedOut);

      const slideObservables = leaderboardRef.current?.slideObservables ?? [];

      const slidesCount = slideObservables.length;

      const isLastSlide = currentSlideIndex === slidesCount - 1;

      if (isLastSlide) {
        setCurrentSlideIndex(-1);
        fetchLeaderboard().then(setNextLeaderboard);
        return;
      }

      const nextSlideIndex =
        currentSlideIndex + 1 < slidesCount ? currentSlideIndex + 1 : 0;

      setCurrentSlideIndex(nextSlideIndex);
    }, SLIDE_ANIMATE_IN_MS + SLIDE_ANIMATE_OUT_MS + duration.slideDurationMs);

    return () => {
      clearTimeout(animatedInTimeout);
      clearTimeout(animateOutTimeout);
      clearTimeout(animatedOutTimeout);
    };
  }, [isReady, currentSlideIndex]);

  useEffect(() => {
    if (currentSlideIndex !== -1 || !nextLeaderboard) return;

    leaderboardRef.current = nextLeaderboard;

    setLeaderboard(nextLeaderboard);
    setNextLeaderboard(undefined);
    setCurrentSlideIndex(0);
  }, [currentSlideIndex, nextLeaderboard]);

  return leaderboardError ? (
    <p className={classes.error}>{leaderboardError.message}</p>
  ) : (
    <div className={classes.leaderboardDisplayContainer}>
      {(leaderboard?.slideObservables ?? []).map((slideObservable, i) =>
        currentSlideIndex === i ? (
          <SlideContainer
            key={`slideContainer-${
              slideObservable.slide.slideId
            }-${leaderboard?.fetchedAt?.toISOString()}`}
            slideObservable={slideObservable}
          >
            {leaderboard && slideObservable.slide.type === SlideType.agent && (
              <Agent
                leaderboard={leaderboard}
                slideObservable={slideObservable}
              />
            )}
            {leaderboard && slideObservable.slide.type === SlideType.queue && (
              <Queue
                leaderboard={leaderboard}
                slideObservable={slideObservable}
              />
            )}
            {slideObservable.slide.type === SlideType.skeleton && (
              <Skeleton
                leaderboard={leaderboard}
                slideObservable={slideObservable}
              />
            )}
          </SlideContainer>
        ) : null
      )}
      <div className={classes.exitButton}>
        <IconButton
          color="primary"
          onClick={() => {
            window.history.back();
          }}
          size="large"
        >
          <ArrowLeft fontSize="large" />
          Exit
        </IconButton>
      </div>
    </div>
  );
}
